import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle, ArrowLeft, Mail } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { getAuthRedirectUrl } from '@/lib/utils/authRedirect';

interface HospitalInfo {
  name: string;
  slug: string;
  email: string;
  phone: string;
  address: string;
}

interface AdminInfo {
  email: string;
  password: string;
  confirmPassword: string;
  name: string;
}

export function SaaSSignUp() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'hospital' | 'admin'>('hospital');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [showEmailConfirmationDialog, setShowEmailConfirmationDialog] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState<string>('');
  
  const [hospitalInfo, setHospitalInfo] = useState<HospitalInfo>({
    name: '',
    slug: '',
    email: '',
    phone: '',
    address: '',
  });
  
  const [adminInfo, setAdminInfo] = useState<AdminInfo>({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
  });

  // Fade in animations on mount
  useEffect(() => {
    setIsVisible(true);
    setFormVisible(true);
  }, []);

  // Reset form animation when step changes
  useEffect(() => {
    setFormVisible(false);
    const timer = setTimeout(() => {
      setFormVisible(true);
    }, 150);
    return () => clearTimeout(timer);
  }, [step]);

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };

  const handleHospitalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Generate slug from hospital name
      const slug = hospitalInfo.slug || generateSlug(hospitalInfo.name);
      
      // Check if slug already exists
      const { data: existing } = await supabase
        .from('hospitals')
        .select('id')
        .eq('slug', slug)
        .single();

      if (existing) {
        setError('A hospital with this name already exists. Please choose a different name.');
        return;
      }

      // Validate form
      if (!hospitalInfo.name || !hospitalInfo.email || !hospitalInfo.phone) {
        setError('Please fill in all required fields (Hospital Name, Email, and Phone Number)');
        return;
      }

      setStep('admin');
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleAdminSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate passwords match
      if (adminInfo.password !== adminInfo.confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      if (adminInfo.password.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }

      const slug = hospitalInfo.slug || generateSlug(hospitalInfo.name);

      // Check if slug still available
      const { data: existing } = await supabase
        .from('hospitals')
        .select('id')
        .eq('slug', slug)
        .single();

      if (existing) {
        setError('A hospital with this name already exists. Please refresh and try again.');
        return;
      }

      // 1. Create Hospital with 14-day trial
      const { data: hospitalData, error: hospitalError } = await supabase
        .from('hospitals')
        .insert({
          name: hospitalInfo.name,
          slug,
          email: hospitalInfo.email,
          phone: hospitalInfo.phone,
          address: hospitalInfo.address,
          subscription_status: 'trial',
          trial_started_at: new Date().toISOString(),
          trial_days: 14,
        })
        .select()
        .single();

      if (hospitalError) {
        console.error('Hospital creation error:', hospitalError);
        throw new Error('Failed to create hospital: ' + hospitalError.message);
      }

      console.log('Hospital created:', hospitalData);

      // 2. Create Admin User
      // Set redirect URL for email confirmation (use production URL for email links)
      const redirectUrl = getAuthRedirectUrl('/confirm-email');
      
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: adminInfo.email,
        password: adminInfo.password,
        options: {
          data: {
            role: 'admin',
            name: adminInfo.name,
            hospital_id: hospitalData.id,
          },
          emailRedirectTo: redirectUrl
        }
      });

      if (authError) {
        // If user creation fails, clean up the hospital
        await supabase.from('hospitals').delete().eq('id', hospitalData.id);
        throw new Error('Failed to create admin user: ' + authError.message);
      }

      if (!authData.user) {
        await supabase.from('hospitals').delete().eq('id', hospitalData.id);
        throw new Error('No user data returned from signup');
      }

      // 3. Update profile with hospital_id
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          hospital_id: hospitalData.id,
          name: adminInfo.name,
          role: 'admin',
        })
        .eq('id', authData.user.id);

      if (profileError) {
        console.error('Profile update error:', profileError);
        // Note: Cannot clean up auth user from client-side (requires service role)
        // User can be cleaned up manually if needed
        throw new Error('Failed to update profile: ' + profileError.message);
      }

      // 4. Create default clinic settings for this hospital
      await supabase
        .from('clinic_settings')
        .insert({
          hospital_id: hospitalData.id,
          name: hospitalInfo.name,
          email: hospitalInfo.email,
          phone: hospitalInfo.phone,
          address: hospitalInfo.address,
        });

      // Success! Show email confirmation dialog
      setRegisteredEmail(adminInfo.email);
      setShowEmailConfirmationDialog(true);
    } catch (err: any) {
      console.error('Complete signup error:', err);
      setError(err.message || 'An error occurred during signup');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card 
        className={`w-full max-w-2xl transition-all duration-700 ease-out ${
          isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'
        }`}
        style={{ transitionDelay: '0.2s' }}
      >
        <CardHeader>
          {/* Back to Home Button */}
          <Link 
            to="/" 
            className={`inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4 transition-all duration-500 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
            style={{ transitionDelay: '0.3s' }}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
          
          <CardTitle 
            className={`text-2xl font-bold transition-all duration-700 ease-out ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: '0.4s' }}
          >
            {step === 'hospital' ? 'Register Your Hospital' : 'Create Admin Account'}
          </CardTitle>
          <CardDescription 
            className={`transition-all duration-700 ease-out ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: '0.5s' }}
          >
            {step === 'hospital' 
              ? 'Enter your hospital information to get started'
              : 'Create your admin account to manage your hospital'
            }
          </CardDescription>
        </CardHeader>
        <CardContent 
          className={`transition-all duration-500 ease-out ${
            formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          {error && (
            <Alert 
              variant="destructive" 
              className={`mb-4 transition-all duration-500 ease-out ${
                formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {step === 'hospital' ? (
            <form 
              onSubmit={handleHospitalSubmit} 
              className={`space-y-4 transition-all duration-500 ease-out ${
                formVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="hospital-name">Hospital Name *</Label>
                  <Input
                    id="hospital-name"
                    value={hospitalInfo.name}
                    onChange={(e) => setHospitalInfo({
                      ...hospitalInfo,
                      name: e.target.value,
                      slug: generateSlug(e.target.value)
                    })}
                    placeholder="e.g., City Medical Center"
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    URL-friendly slug: {hospitalInfo.slug || generateSlug(hospitalInfo.name)}
                  </p>
                </div>
              </div>

              <div>
                <Label htmlFor="hospital-email">Hospital Email *</Label>
                <Input
                  id="hospital-email"
                  type="email"
                  value={hospitalInfo.email}
                  onChange={(e) => setHospitalInfo({ ...hospitalInfo, email: e.target.value })}
                  placeholder="contact@yourhospital.com"
                  required
                />
              </div>

              <div>
                <Label htmlFor="hospital-phone">Phone Number *</Label>
                <Input
                  id="hospital-phone"
                  type="tel"
                  value={hospitalInfo.phone}
                  onChange={(e) => setHospitalInfo({ ...hospitalInfo, phone: e.target.value })}
                  placeholder="+1234567890"
                  required
                />
              </div>

              <div>
                <Label htmlFor="hospital-address">Address</Label>
                <Textarea
                  id="hospital-address"
                  value={hospitalInfo.address}
                  onChange={(e) => setHospitalInfo({ ...hospitalInfo, address: e.target.value })}
                  placeholder="Street Address, City, State, ZIP"
                  rows={2}
                />
              </div>

              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/login')}
                  className="flex-1 transition-all duration-300 hover:scale-105"
                >
                  Back to Login
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 transition-all duration-300 hover:scale-105" 
                  disabled={loading}
                >
                  {loading ? 'Checking...' : 'Next: Create Admin'}
                </Button>
              </div>
            </form>
          ) : (
            <form 
              onSubmit={handleAdminSubmit} 
              className={`space-y-4 transition-all duration-500 ease-out ${
                formVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
              }`}
            >
              <div>
                <Label htmlFor="admin-name">Your Name *</Label>
                <Input
                  id="admin-name"
                  value={adminInfo.name}
                  onChange={(e) => setAdminInfo({ ...adminInfo, name: e.target.value })}
                  placeholder="Dr. John Doe"
                  required
                />
              </div>

              <div>
                <Label htmlFor="admin-email">Email Address *</Label>
                <Input
                  id="admin-email"
                  type="email"
                  value={adminInfo.email}
                  onChange={(e) => setAdminInfo({ ...adminInfo, email: e.target.value })}
                  placeholder="admin@yourhospital.com"
                  required
                />
              </div>

              <div>
                <Label htmlFor="admin-password">Password *</Label>
                <Input
                  id="admin-password"
                  type="password"
                  value={adminInfo.password}
                  onChange={(e) => setAdminInfo({ ...adminInfo, password: e.target.value })}
                  placeholder="At least 6 characters"
                  required
                />
              </div>

              <div>
                <Label htmlFor="admin-confirm-password">Confirm Password *</Label>
                <Input
                  id="admin-confirm-password"
                  type="password"
                  value={adminInfo.confirmPassword}
                  onChange={(e) => setAdminInfo({ ...adminInfo, confirmPassword: e.target.value })}
                  placeholder="Re-enter password"
                  required
                />
              </div>

              <div className="bg-blue-50 p-4 rounded-md">
                <p className="text-sm text-blue-900">
                  <strong>Hospital:</strong> {hospitalInfo.name}<br />
                  <strong>Email:</strong> {hospitalInfo.email}
                </p>
              </div>

              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep('hospital')}
                  className="flex-1 transition-all duration-300 hover:scale-105"
                  disabled={loading}
                >
                  Back
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 transition-all duration-300 hover:scale-105" 
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Complete Registration'}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Email Confirmation Dialog */}
      <AlertDialog open={showEmailConfirmationDialog} onOpenChange={setShowEmailConfirmationDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-full">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
              <AlertDialogTitle className="text-xl">Registration Successful!</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-base pt-2">
              <p className="mb-3">
                A confirmation email has been sent to <strong className="text-gray-900">{registeredEmail}</strong>.
              </p>
              <p className="text-sm text-gray-600">
                Please check your email inbox and click on the confirmation link to verify your account. 
                You will only be able to login to the website after confirming your email address.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => {
                setShowEmailConfirmationDialog(false);
                navigate('/login', { 
                  state: { 
                    message: 'Please check your email to confirm your account before logging in.',
                    hospitalCreated: true 
                  } 
                });
              }}
              className="w-full sm:w-auto"
            >
              Continue to Login
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

